import { PageHero, CTASection } from "@/components/Bits";
import { SectionLabel } from "@/components/SectionLabel";
import { Reveal } from "@/components/Reveal";
import { FlaskConical, Cloud, Brain, LineChart, GraduationCap, Users2, Cog, Layers, Sparkles, Award, CheckCircle2, Wrench, Trophy, Medal, GlobeLock, Globe2, Star, Crown, ScrollText, ArrowUpRight, Monitor, Smartphone, ShieldCheck } from "lucide-react";

const B = "https://customer-assets-rejwkqb3.emergentagent.net/job_creator-stem-schools/artifacts/";
const HERO_IMG = B + "mshg4mb2_Screenshot%202026-01-31%20155637.png";

const STUDENT_ACTIONS = [
  "Observe demonstrations",
  "Perform experiments",
  "Verify scientific concepts",
  "Apply concepts to solve problems",
  "Present and defend their reasoning",
  "Learn through continuous assessment",
];

const OFFERINGS = [
  { icon: Layers,      hex: "#2E6BB0", t: "PBL-TDLE Academic Framework", d: "Curriculum integration, concept development sequence, problem-solving pedagogy, demonstration planning, experimental learning, assessment framework, classroom implementation strategy and laboratory integration." },
  { icon: FlaskConical, hex: "#1E9E8A", t: "Concept Labs",                d: "Hundreds of global-standard scientific apparatus supporting thousands of experiments across Physics, Chemistry, Biology and Mathematics — Primary through A Level. Every experiment verifies a specific concept before it is used for problem solving." },
  { icon: Cloud,        hex: "#F0A831", t: "Complete Digital Academic Ecosystem", d: "CoPE-App generates a purpose-built digital lesson plan for every grade (1 to 12) and all four subjects — Physics, Chemistry, Biology and Mathematics. Each plan includes a complete list of concepts, curated problems, concept-linked experiments, the exact apparatus for each experiment and detailed step-by-step solutions to every problem — with Teacher App, Learner App, Parent Portal, School Leadership Dashboard, AI Analytics, Assessment Engine and Performance Tracking all in one integrated cloud platform." },
  { icon: Sparkles,     hex: "#8B5CF6", t: "Research Scientist Mentorship", d: "Direct academic mentorship from M. A. Rehman — Founder, Research Scientist and Physicist with 25+ years of research and teaching experience. Ongoing guidance, not one-time workshops." },
  { icon: Cog,          hex: "#E14C7A", t: "System-Driven Methodology",   d: "Every lesson follows a predefined sequence. Every concept is verified. Every experiment is linked to learning objectives. Every assessment measures concept development. Every student follows the same structured pathway to mastery." },
  { icon: Wrench,       hex: "#2E6BB0", t: "Cloud-Based Academic Management System", d: "Centralised platform connecting students, teachers, coordinators, principals and parents — real-time lesson tracking, experiment records, attendance, homework, FCA assessments, portfolios, communication, analytics and reports." },
  { icon: Users2,       hex: "#1E9E8A", t: "Teacher App",                  d: "Structured lesson plans, demonstration guides, experiment recording, assessments, student-progress monitoring, concept-completion tracking and academic guidance in one place." },
  { icon: GraduationCap, hex: "#F0A831", t: "Learner App",                 d: "Access to concepts, demonstration replays, assignments, problem-solving practice, progress tracking, personalised feedback and analytics — with real transparency for parents." },
  { icon: Brain,        hex: "#8B5CF6", t: "Analytical Intelligence System", d: "Analytics engine that surfaces concept mastery, learning gaps, engagement, assessment trends, teacher effectiveness, experiment completion, problem-solving performance and cohort progress." },
  { icon: LineChart,    hex: "#E14C7A", t: "NTP Collaboration",            d: "Partner schools integrate the NextGen Training Program — advanced mentoring for JEE, NEET, Olympiads, AP, AMC, BPhO, IChO, IBO, PhysicsBowl, university admissions, scholarships, research and innovation." },
];

const BENEFITS = [
  "Highly engaged classrooms",
  "Strong conceptual learning",
  "Scientific thinking and inquiry",
  "Improved problem-solving ability",
  "Better examination performance",
  "Enhanced teacher effectiveness",
  "Real-time academic monitoring",
  "Increased accountability",
  "Data-driven decision making",
  "Stronger parent confidence",
  "Improved school reputation",
  "International competition readiness",
  "Better university admissions and scholarship opportunities",
  "Sustainable academic excellence — proven, system-driven methodology",
];

const KEY_ACHIEVEMENTS = [
  { icon: Trophy,     hex: "#F0A831", t: "100% Results in Cambridge AS & A Level Exams" },
  { icon: Crown,      hex: "#E14C7A", t: "Cambridge Highest OCLA Award in IGCSE Board Examinations" },
  { icon: GraduationCap, hex: "#2E6BB0", t: "100% Students Receiving International Admission Offers" },
  { icon: ScrollText, hex: "#1E9E8A", t: "Multiple Students Receiving 100% Scholarships at Top Universities" },
  { icon: Star,       hex: "#8B5CF6", t: "Outstanding Achievements in American Mathematics Competitions (AMC)" },
  { icon: Medal,      hex: "#F0A831", t: "International Medals in British Physics Olympiad (BPhO)" },
  { icon: Globe2,     hex: "#2E6BB0", t: "International Recognition in BIOlympiad, PhysicsBOWL and Other Global Competitions" },
  { icon: Award,      hex: "#1E9E8A", t: "More than 1500 Gold Medals in SOF Olympiads" },
  { icon: Trophy,     hex: "#E14C7A", t: "Two International Rank 1 Achievements in Global Olympiads" },
];

const TRAINING = [
  { w: "Weeks 1–2",  t: "Procurement & Onboarding",                     d: "Cohort-level concept diagnostics. Teacher orientation on IDEA pedagogy and the PBL-TDLE philosophy." },
  { w: "Weeks 3–4",  t: "Concept Lab Setup",                             d: "Installation of globally standardised apparatus. Teachers rehearse first-term demonstrations subtopic by subtopic." },
  { w: "Weeks 5–6",  t: "Curriculum Concept Mapping & CoPE-App Design",  d: "The school and board curriculum is mapped for the concepts; problems and experiments are then designed by choosing the appropriate apparatus sets." },
  { w: "Weeks 7–10", t: "First PBL-TDLE Cycles",                         d: "First PBL-TDLE cycles run with faculty. Observe → Investigate → Measure → Analyse → Verify → Apply." },
  { w: "Term 2+",    t: "Deep Practice + NTP",                           d: "Level-2 and Level-3 problems, olympiad tracks and NextGen Training Program integration for high-potential students." },
];

export default function WhyPblTdle() {
  return (
    <div data-testid="why-pbl-tdle-page">
      <PageHero
        n="P"
        label="Why PBL-TDLE?"
        lines={["A complete academic", "transformation system."]}
        intro="Not a training programme. Not a collection of resources. A research-based system that changes how Science and Mathematics are taught, learned, assessed and managed."
      />

      {/* PBL-TDLE replaces passive learning */}
      <section className="border-t border-white/10 bg-[#070d18]" data-testid="why-students-do">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-20 md:py-28">
          <SectionLabel n="A">What Students Actually Do</SectionLabel>
          <h2 className="font-display text-3xl md:text-5xl text-white mt-6 tracking-tight leading-tight max-w-4xl font-light">
            PBL-TDLE replaces passive learning with an active process.
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-12">
            {STUDENT_ACTIONS.map((s, i) => (
              <Reveal key={s} delay={(i % 3) * 0.06} className="border border-white/10 rounded-xl p-6 bg-[#050B14] hover:border-accent transition-colors">
                <div className="font-display text-3xl text-accent">{String(i + 1).padStart(2, "0")}</div>
                <p className="text-white/80 leading-snug mt-3">{s}</p>
              </Reveal>
            ))}
          </div>
          <p className="text-white/60 leading-relaxed mt-10 max-w-3xl">
            Every concept becomes <span className="text-white">measurable</span>. Every lesson becomes an <span className="text-white">investigation</span>. Every classroom becomes a <span className="text-accent">Concept Lab</span>.
          </p>
        </div>
      </section>

      {/* What We Provide — 10 offerings */}
      <section className="border-t border-white/10" data-testid="why-offerings">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-20 md:py-28">
          <SectionLabel n="B">What a Partnership Includes</SectionLabel>
          <h2 className="font-display text-3xl md:text-5xl text-white mt-6 tracking-tight leading-tight max-w-4xl font-light">
            Complete academic, technological and research support.
          </h2>
          <div className="grid md:grid-cols-2 gap-6 mt-14">
            {OFFERINGS.map((o, i) => {
              const Icon = o.icon;
              return (
                <Reveal key={o.t} delay={(i % 2) * 0.06} className="border border-white/10 rounded-2xl p-7 bg-[#050B14] hover:border-white/25 transition-colors flex items-start gap-5" data-testid={`why-offering-${i}`}>
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center border shrink-0" style={{ borderColor: o.hex, backgroundColor: `${o.hex}22` }}>
                    <Icon className="w-6 h-6" style={{ color: o.hex }} />
                  </div>
                  <div className="min-w-0">
                    <div className="font-mono-label text-[11px] tracking-label uppercase" style={{ color: o.hex }}>Included</div>
                    <h3 className="font-display text-xl md:text-2xl text-white mt-2 tracking-tight leading-snug">{o.t}</h3>
                    <p className="text-white/65 leading-relaxed mt-3 text-sm">{o.d}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Cloud platform + Apps · Live at tce-pbl.uk */}
      <section className="border-t border-white/10" data-testid="why-platform">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-20 md:py-28">
          <div className="flex items-center gap-3">
            <Cloud className="w-5 h-5 text-accent" />
            <SectionLabel n="B · 2">The Cloud Platform &amp; Apps</SectionLabel>
          </div>
          <h2 className="font-display text-3xl md:text-5xl text-white mt-6 tracking-tight leading-tight max-w-5xl font-light">
            One <span className="text-accent">cloud platform</span> — separate apps for teachers, students and school leaders.
          </h2>
          <p className="text-white/70 leading-relaxed mt-6 max-w-3xl">
            Every partner school receives access to <span className="text-white font-semibold">tce-pbl.uk</span> — the live cloud home of the CoPE-App. Teachers plan and run their lessons from the Teacher Portal; students receive assignments, submit work and track their concept-mastery from the Student Portal; and school leaders monitor implementation, cohort progress and outcomes from the Admin dashboard.
          </p>

          <div className="grid md:grid-cols-3 gap-5 mt-12" data-testid="why-platform-cards">
            {[
              {
                icon: Monitor,
                hex: "#2E6BB0",
                t: "Teacher Portal",
                d: "Concept lists, tailor-made lesson plans, experiments, apparatus, live formative assessment and per-student mastery tracking — all from one dashboard.",
              },
              {
                icon: Smartphone,
                hex: "#F0A831",
                t: "Student App",
                d: "Daily concept work, assignments, problem sets, submissions and instant feedback — with topic-wise mastery analytics visible to student and parent.",
              },
              {
                icon: ShieldCheck,
                hex: "#1E9E8A",
                t: "School Leader Dashboard",
                d: "Real-time PBL-TDLE implementation monitoring, cohort-level academic quality assurance and evidence-based reporting for principals and trustees.",
              },
            ].map((p, i) => {
              const Icon = p.icon;
              return (
                <Reveal
                  key={p.t}
                  delay={i * 0.06}
                  className="relative border border-white/10 rounded-2xl p-7 bg-[#050B14] hover:border-accent/60 transition-colors overflow-hidden group"
                  data-testid={`why-platform-card-${i}`}
                >
                  <div className="absolute -top-8 -right-8 w-28 h-28 rounded-full blur-3xl opacity-25 group-hover:opacity-50 transition-opacity" style={{ backgroundColor: p.hex }} />
                  <div className="relative">
                    <div className="w-11 h-11 rounded-xl flex items-center justify-center border" style={{ borderColor: p.hex, backgroundColor: `${p.hex}22` }}>
                      <Icon className="w-5 h-5" style={{ color: p.hex }} />
                    </div>
                    <div className="font-display text-xl text-white mt-4 tracking-tight">{p.t}</div>
                    <p className="text-white/65 text-sm leading-relaxed mt-3">{p.d}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>

          {/* CTA — open the live platform */}
          <div className="mt-12 rounded-2xl border border-accent/40 bg-gradient-to-br from-[#0B1B30] to-[#050B14] p-6 md:p-8 flex flex-col md:flex-row md:items-center md:justify-between gap-5 max-w-5xl" data-testid="why-platform-cta-box" style={{ color: "#ffffff" }}>
            <div>
              <div className="font-mono-label text-[11px] tracking-label uppercase text-accent">Live · Already in use by partner schools</div>
              <p className="font-display text-xl md:text-2xl mt-2 leading-snug tracking-tight" style={{ color: "#ffffff" }}>
                Try the CoPE-App yourself — <span className="text-accent">tce-pbl.uk</span>
              </p>
              <p className="text-sm mt-2 max-w-2xl" style={{ color: "rgba(255,255,255,0.65)" }}>
                Explore the teacher and student experience before you decide. Ask for a demo login for your leadership team.
              </p>
            </div>
            <a
              href="https://tce-pbl.uk/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="why-platform-cta"
              className="inline-flex items-center gap-2 bg-accent text-[#050B14] px-6 py-3 rounded-full text-sm font-semibold hover:bg-accent/90 transition-colors shrink-0 self-start md:self-auto"
            >
              Open the CoPE-App <ArrowUpRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="border-t border-white/10 bg-[#070d18]" data-testid="why-benefits">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-20 md:py-28">
          <div className="flex items-center gap-3">
            <Award className="w-5 h-5 text-accent" />
            <SectionLabel n="C">Benefits of Collaboration</SectionLabel>
          </div>
          <h2 className="font-display text-3xl md:text-5xl text-white mt-6 tracking-tight leading-tight max-w-4xl font-light">
            Transformation across every level of the academic ecosystem.
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3 mt-12">
            {BENEFITS.map((b) => (
              <div key={b} className="flex items-start gap-3 border border-white/10 rounded-xl p-5 hover:border-accent transition-colors">
                <CheckCircle2 className="w-5 h-5 text-c-teal shrink-0 mt-0.5" />
                <span className="text-white/80 leading-relaxed text-sm">{b}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Key Achievements — proof of PBL-TDLE success */}
      <section className="border-t border-white/10" data-testid="why-key-achievements">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-20 md:py-28">
          <div className="flex items-center gap-3">
            <Trophy className="w-5 h-5 text-accent" />
            <SectionLabel n="D">Key Achievements</SectionLabel>
          </div>
          <h2 className="font-display text-3xl md:text-5xl text-white mt-6 tracking-tight leading-tight max-w-4xl font-light">
            Unprecedented success from <span className="text-accent">The Creator International School</span>.
          </h2>
          <p className="text-white/60 leading-relaxed mt-6 max-w-3xl">
            Concrete, verifiable outcomes from the very first school that adopted the PBL-TDLE system — the same academic framework we now bring to every partner school.
          </p>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 mt-14" data-testid="why-achievements-grid">
            {KEY_ACHIEVEMENTS.map((a, i) => {
              const Icon = a.icon;
              return (
                <Reveal
                  key={a.t}
                  delay={(i % 3) * 0.06}
                  className="relative border border-white/10 rounded-2xl p-7 bg-[#050B14] hover:border-accent/60 transition-colors overflow-hidden group"
                  data-testid={`why-achievement-${i}`}
                >
                  <div className="absolute -top-6 -right-6 w-24 h-24 rounded-full blur-2xl opacity-30 group-hover:opacity-60 transition-opacity" style={{ backgroundColor: a.hex }} />
                  <div className="relative flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center border shrink-0" style={{ borderColor: a.hex, backgroundColor: `${a.hex}22` }}>
                      <Icon className="w-6 h-6" style={{ color: a.hex }} />
                    </div>
                    <div className="min-w-0">
                      <div className="font-mono-label text-[11px] tracking-label uppercase" style={{ color: a.hex }}>Achievement · {String(i + 1).padStart(2, "0")}</div>
                      <p className="text-white font-display text-lg md:text-xl mt-2 tracking-tight leading-snug">{a.t}</p>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>

          <p className="text-white/50 leading-relaxed mt-12 max-w-3xl text-sm italic">
            These outcomes are the direct result of the PBL-TDLE methodology, Concept Labs, structured assessment framework and continuous research-scientist mentorship — the same complete system offered to every collaborating school.
          </p>
        </div>
      </section>

      {/* Teacher training / Implementation timeline — moved from Framework page */}
      <section className="border-t border-white/10 bg-[#070d18]" data-testid="why-training">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-20 md:py-28">
          <SectionLabel n="E">Teacher Training &amp; Implementation Timeline</SectionLabel>
          <h2 className="font-display text-3xl md:text-5xl text-white mt-6 tracking-tight leading-tight max-w-4xl font-light">
            From partnership signed to first PBL-TDLE cycle — <span className="text-accent">in ten weeks</span>.
          </h2>
          <p className="text-white/60 leading-relaxed mt-6 max-w-3xl">
            A structured, phased rollout: apparatus, training, curriculum mapping, and the first live PBL-TDLE cycles run alongside your existing teachers — with our research-educators embedded on the ground.
          </p>
          <div className="grid md:grid-cols-5 gap-4 mt-12">
            {TRAINING.map((t, i) => {
              const colors = ["text-c-blue", "text-c-teal", "text-c-amber", "text-c-violet", "text-c-rose"];
              return (
                <Reveal key={t.w} delay={i * 0.06} className="border border-white/10 rounded-xl p-6 hover:border-accent transition-colors bg-[#050B14]" data-testid={`why-training-${i}`}>
                  <span className={`font-mono-label text-[11px] tracking-label uppercase ${colors[i]}`}>{t.w}</span>
                  <h3 className="font-display text-lg md:text-xl text-white mt-3 tracking-tight leading-tight">{t.t}</h3>
                  <p className="text-white/55 leading-relaxed mt-3 text-sm">{t.d}</p>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Closing manifesto */}
      <section className="border-t border-white/10 bg-cream text-ink" data-testid="why-closing">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-20 md:py-28">
          <SectionLabel n="✦" dark>Building Future Researchers, Innovators & Leaders</SectionLabel>
          <Reveal>
            <blockquote className="font-display text-3xl md:text-5xl text-ink leading-[1.1] tracking-tight mt-10 max-w-5xl font-light">
              “More than a methodology — a comprehensive academic ecosystem combining research-based pedagogy, global-standard laboratories, digital technology, continuous mentorship and analytical intelligence.”
            </blockquote>
            <p className="text-ink/65 text-lg leading-relaxed mt-8 max-w-3xl">
              Together with our partner schools, we are building classrooms where every student develops concepts deeply, solves problems confidently, thinks scientifically — and is prepared to contribute to research, innovation and national development.
            </p>
          </Reveal>
        </div>
      </section>

      <CTASection />
    </div>
  );
}
